import React, {createContext, useCallback, useContext, useEffect, useState,} from 'react'
import _ from 'lodash'
import {useLocation} from 'react-use'
import {useRouteSearchParam,} from 'envs/_current/history'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import {messageOnError} from '../factory/createErrorEvent'
import {
    devotion_create_Api,
    devotion_createChecklist_Api,
    devotion_createIntention_Api,
    devotion_delete_Api,
    devotion_deleteChecklist_Api,
    devotion_deleteIntention_Api,
    devotion_dismissReminder_Api,
    devotion_edit_Api,
    devotion_editChecklist_Api,
    devotion_editIntention_Api,
    devotion_get_Api,
    devotion_join_Api,
    devotion_leave_Api,
    devotion_recordPractice_Api,
    devotion_saveChecklist_Api,
    devotion_snoozeReminder_Api,
    devotion_updateParticipant_Api,
} from 'apis/devotion'
import {useHistory} from "../../../modules/navigation/useRouter";
import {copyLocationParam, getRouteSearchParams} from "../../../envs/_current/history";
import {devotion_recordPracticeByGroup_Api} from "../../../apis";
import {staticPaths} from "../../../routes/staticPaths";
import {NotificationActionTypes} from "../factory/createNotification";
import {useMessageNotification} from "../factory/createMessageNotification";

// ─── Constants ────────────────────────────────────────────────────────────────

export const PRACTICE_STATUS = {
    STARTED: 1,
    COMPLETED: 9,
    SKIPPED: -9,
}

export const PRAYER_FORMS = [
    {value: 'rosary', label: 'rosary', description: 'rosary context', href: undefined, groupable: true, duration: 20},
    {value: 'divine_mercy', label: 'divine mercy', description: 'divine mercy context', href: undefined, groupable: true, duration: 15},
    {value: 'holy_spirit_9_day_novena', href: undefined, label: 'hs novena title', description: 'hs novena context', groupable: true, duration: 30},
    {value: 'angelus', label: 'ang title', description: 'ang context', href: undefined, groupable: true, duration: 30},
    {value: 'stations_of_cross', label: 'stc title', description: 'stc context', href: undefined, groupable: false, duration: 30},
    {value: 'eucharistic_adoration', label: 'ea title', description: 'ea context', href: undefined, groupable: false, duration: 60},

    {
        value: 'liturgy_of_the_hours',
        label: 'liturgy of the hours',
        description: 'liturgy of the hours context',
        group: 'traditional_prayers', href: "/liturgical"
    },
    {
        value: 'pope_prayer_intentions',
        label: "pope's prayer intentions",
        description: 'popes prayer intentions context',
        group: 'traditional_prayers', href: "/devotions?devotion_tab=mine&tab=intentions&methods_tab=prayers"
    }

]

// ─── Context ──────────────────────────────────────────────────────────────────

export const DevotionContext = createContext({})

// ─── Provider ─────────────────────────────────────────────────────────────────

export const DevotionProvider = ({children}) => {
    const history = useHistory()
    const location = useLocation()
    const t = useTranslate()
    const onError = messageOnError(t)
    const {successMessage, errorMessage} = useMessageNotification()

    // URL params
    const devotionId = useRouteSearchParam('d') ?? null

    // ── State ──────────────────────────────────────────────────────────────────
    const [currentDevotion, setCurrentDevotion] = useState(null)
    const [currentParticipant, setCurrentParticipant] = useState(null)
    const [currentPractice, setCurrentPractice] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showPracticePanel, setShowPracticePanel] = useState(false)
    const [checklists, setChecklists] = useState([])

    const refreshToken = () => {
        const searchParams = getRouteSearchParams();
        searchParams.set('refresh_token', Date.now());
        history.replace({
            ...copyLocationParam(window.location ?? {}),
            pathname: window.location.pathname,
            search: '?' + searchParams.toString(),
        });
    }


    // ── Actions: Get devotion detail ──────────────────────────────────────────
    const {handleAsyncAction: getDevotionById} = useAsyncAction({
        onError,
        apiInfo: devotion_get_Api,
        onSuccess: (result, data) => {
            const item = _.get(data, 'response.data', null)
            if (item) {
                setCurrentDevotion(item)
                setCurrentParticipant(item?.current_participant)
                setCurrentPractice(item?.current_practice)
            }
        },
    })

    // ── Actions: Create ───────────────────────────────────────────────────────

    const {handleAsyncAction: createDevotion} = useAsyncAction({
        onError,
        apiInfo: devotion_create_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item) {
                setCurrentDevotion(item)
                setShowCreateModal(false)
                refreshToken()
            }
        },
    })

    // ── Actions: Edit ─────────────────────────────────────────────────────────

    const {handleAsyncAction: editDevotion} = useAsyncAction({
        onError,
        apiInfo: devotion_edit_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item) {
                setCurrentDevotion(item)
            }
        },
    })

    // ── Actions: Delete ───────────────────────────────────────────────────────

    const {handleAsyncAction: deleteDevotion} = useAsyncAction({
        onError,
        apiInfo: devotion_delete_Api,
        onSuccess: () => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            setCurrentDevotion(null)
            refreshToken()
            history.push(staticPaths.devotions.pathname)
        },
    })

    // ── Actions: Join ─────────────────────────────────────────────────────────

    const {handleAsyncAction: joinDevotion} = useAsyncAction({
        onError,
        apiInfo: devotion_join_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            // item.devotions: mảng — 1 group giờ có thể gắn nhiều devotion (vd Kinh Sáng/Kinh Tối
            // chung 1 group), lấy đúng devotion vừa join theo id đã gửi lên (action.query.id),
            // không phải mảng đầu tiên.
            const joinedDevotionId = _.get(data, 'action.query.id', null)
            if (item) {
                const joinedDevotion = (item.devotions ?? []).find(d => d.id === joinedDevotionId)
                    ?? item.devotions?.[0]
                if (joinedDevotion) setCurrentDevotion(joinedDevotion)
                setCurrentParticipant(item)
            }

            refreshToken()
        },
    })

    // ── Actions: Leave ────────────────────────────────────────────────────────

    const {handleAsyncAction: leaveDevotion} = useAsyncAction({
        onError,
        apiInfo: devotion_leave_Api,
        onSuccess: () => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            setCurrentParticipant(null)
            if (currentDevotion?.id) getDevotionById({}, {id: currentDevotion.id})
            refreshToken()
        },
    })

    // ── Actions: Update participant settings ──────────────────────────────────

    const {handleAsyncAction: updateParticipant} = useAsyncAction({
        onError,
        apiInfo: devotion_updateParticipant_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item) {
                setCurrentParticipant(item)
            }
        },
    })

    // ── Actions: Practice ─────────────────────────────────────────────────────

    const {handleAsyncAction: recordPractice} = useAsyncAction({
        onError,
        apiInfo: devotion_recordPractice_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item) {
                setCurrentPractice(item)
                setCurrentDevotion(prev => prev ? ({...prev, current_practice: item}) : prev)
                setCurrentParticipant(prev => prev ? ({...prev, current_practice: item}) : prev)
            }
            setShowPracticePanel(false)
        },
    })

    const {handleAsyncAction: recordPracticeByGroup} = useAsyncAction({
        onError,
        apiInfo: devotion_recordPracticeByGroup_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            setShowPracticePanel(false)
            refreshToken()
        },
    })

    // ── Actions: Reminder ─────────────────────────────────────────────────────

    const {handleAsyncAction: snoozeReminder} = useAsyncAction({
        onError,
        apiInfo: devotion_snoozeReminder_Api,
    })

    const {handleAsyncAction: dismissReminder} = useAsyncAction({
        onError,
        apiInfo: devotion_dismissReminder_Api,
    })

    // ── Actions: Checklists ──────────────────────────────────────────────────

    // const {handleAsyncAction: getChecklists} = useAsyncAction({
    //     onError,
    //     apiInfo: devotion_getChecklists_Api,
    //     onSuccess: (result, data) => {
    //         const items = _.get(data, 'response.data', [])
    //         setChecklists(Array.isArray(items) ? items : [])
    //     },
    // })

    const {handleAsyncAction: createChecklist} = useAsyncAction({
        onError,
        apiInfo: devotion_createChecklist_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item && currentDevotion) {
                setCurrentDevotion(prev => ({
                    ...prev,
                    checklists: [item, ...(prev.checklists ?? [])],
                }))
            }
        },
    })

    const {handleAsyncAction: saveChecklist} = useAsyncAction({
        onError,
        apiInfo: devotion_saveChecklist_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item && currentDevotion) {
                setCurrentDevotion(prev => ({
                    ...prev,
                    checklists: (prev.checklists ?? []).map(i => i.id === item.id ? item : i),
                }))
            }
        },
    })

    const {handleAsyncAction: editChecklist} = useAsyncAction({
        onError,
        apiInfo: devotion_editChecklist_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            setCurrentDevotion(prev => ({
                ...prev,
                checklists: (prev.checklists ?? []).map(i => i.id === item.id ? item : i),
            }))
        },
    })

    const {handleAsyncAction: deleteChecklist} = useAsyncAction({
        onError,
        apiInfo: devotion_deleteChecklist_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            setCurrentDevotion(prev => ({
                ...prev,
                checklists: (prev.checklists ?? []).filter(i => i.id !== item.id),
            }))
        },
    })

    // ── Actions: Intentions ───────────────────────────────────────────────────

    const {handleAsyncAction: createIntention} = useAsyncAction({
        onError,
        apiInfo: devotion_createIntention_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item && currentDevotion) {
                setCurrentDevotion(prev => ({
                    ...prev,
                    intentions: [item, ...(prev.intentions ?? [])],
                }))
            }
            // currentDevotion chỉ là state cục bộ của modal đang mở — các list khác
            // (vd /devotions) đọc dữ liệu từ cache response riêng, cần refresh_token
            // để buộc fetch lại, không thì hiển thị cũ dù API đã thành công.
            refreshToken()
        },
    })

    const {handleAsyncAction: editIntention} = useAsyncAction({
        onError,
        apiInfo: devotion_editIntention_Api,
        onSuccess: (result, data) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (item && currentDevotion) {
                setCurrentDevotion(prev => ({
                    ...prev,
                    intentions: (prev.intentions ?? []).map(i => i.id === item.id ? item : i),
                }))
            }
            refreshToken()
        },
    })

    const {handleAsyncAction: deleteIntention} = useAsyncAction({
        onError,
        apiInfo: devotion_deleteIntention_Api,
        onSuccess: (result, data, params) => {
            successMessage(NotificationActionTypes.INFORMATION, t)
            const item = _.get(data, 'response.data', null)
            if (currentDevotion) {
                setCurrentDevotion(prev => ({
                    ...prev,
                    intentions: (prev.intentions ?? []).filter(i => i.id !== item.id),
                    current_intention: prev.current_intention?.id === item.id ? null : prev.current_intention,
                    intention: prev.intention?.id === item.id ? null : prev.intention,
                }))
            }
            refreshToken()
        },
    })

    // ── Helpers ───────────────────────────────────────────────────────────────
    const handleOpenCreate = useCallback(() => {
        setShowCreateModal(true)
    }, [])

    const handleCloseCreate = useCallback(() => {
        setShowCreateModal(false)
    }, [])

    const handleOpenEdit = useCallback(() => {
        setShowEditModal(true)
    }, [])

    const handleCloseEdit = useCallback(() => {
        setShowEditModal(false)
    }, [])

    const handleOpenPractice = useCallback(() => {
        setShowPracticePanel(true)
    }, [])

    const handleClosePractice = useCallback(() => {
        setShowPracticePanel(false)
    }, [])

    // ── Init: load devotion theo URL param ?d= ────────────────────────────────

    useEffect(() => {
        if (devotionId && devotionId !== currentDevotion?.id) {
            getDevotionById({}, {id: devotionId})
        }
    }, [devotionId])

    // ─── Value ────────────────────────────────────────────────────────────────

    const value = {
        // State
        currentDevotion,
        currentParticipant,
        currentPractice,
        showCreateModal,
        showEditModal,
        showPracticePanel,

        // Setters
        setCurrentDevotion,
        setCurrentParticipant,
        setCurrentPractice,

        // Actions — Devotion
        getDevotionById,
        createDevotion,
        editDevotion,
        deleteDevotion,

        // Actions — Participant
        joinDevotion,
        leaveDevotion,
        updateParticipant,

        // Actions — Practice
        recordPractice,
        recordPracticeByGroup,

        // Actions — Reminder
        snoozeReminder,
        dismissReminder,

        // Actions — Checklists
        // getChecklists,
        createChecklist,
        saveChecklist,
        editChecklist,
        deleteChecklist,

        // Actions — Intention
        createIntention,
        editIntention,
        deleteIntention,

        // UI helpers
        handleOpenCreate,
        handleCloseCreate,
        handleOpenEdit,
        handleCloseEdit,
        handleOpenPractice,
        handleClosePractice,
    }

    return (
        <DevotionContext.Provider value={value}>
            {children}
        </DevotionContext.Provider>
    )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useDevotion = () => useContext(DevotionContext)